// @ts-nocheck
if (!customElements.get('image-canvas-simulation')) {
  /** Decoded template images keyed by URL, shared by every canvas on the page. */
  const imageCache = new Map();

  /**
   * The most recent upload only. Templates are small and finite so they are kept,
   * but a decoded photo can be tens of megabytes and there is no reason to hold on
   * to one the customer has replaced.
   *
   * @type {{src: string, promise: Promise<HTMLImageElement>} | null}
   */
  let uploadCache = null;

  /**
   * Downscaled copies of an upload keyed by target size. They are held per image so
   * they are released along with the upload they were made from.
   *
   * @type {WeakMap<HTMLImageElement, Map<string, HTMLCanvasElement>>}
   */
  const scaledSourceCache = new WeakMap();

  /**
   * Shrinks an upload to roughly the size it is drawn at. A single drawImage only
   * samples a few source pixels per output pixel, so a photo drawn at a fraction of
   * its size comes out grainy. Halving step by step averages every pixel instead.
   *
   * @param {HTMLImageElement} img - The decoded upload.
   * @param {number} targetWidth - The width of the window it is drawn into.
   * @param {number} targetHeight - The height of the window it is drawn into.
   * @returns {HTMLImageElement | HTMLCanvasElement} A source at most twice the target.
   */
  function getScaledSource(img, targetWidth, targetHeight) {
    // Cover the window on both axes so the warp never has to enlarge the source.
    const scale = Math.max(targetWidth / img.width, targetHeight / img.height);

    if (!Number.isFinite(scale) || scale >= 0.5) return img;

    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));
    const key = `${width}x${height}`;
    let sizes = scaledSourceCache.get(img);

    if (!sizes) {
      sizes = new Map();
      scaledSourceCache.set(img, sizes);
    }

    const cached = sizes.get(key);

    if (cached) return cached;

    let source = img;
    let sourceWidth = img.width;
    let sourceHeight = img.height;

    while (sourceWidth / 2 >= width && sourceHeight / 2 >= height) {
      const step = document.createElement('canvas');

      step.width = Math.round(sourceWidth / 2);
      step.height = Math.round(sourceHeight / 2);

      const stepCtx = step.getContext('2d');

      stepCtx.imageSmoothingEnabled = true;
      stepCtx.imageSmoothingQuality = 'high';
      stepCtx.drawImage(source, 0, 0, step.width, step.height);

      source = step;
      sourceWidth = step.width;
      sourceHeight = step.height;
    }

    const scaled = document.createElement('canvas');

    scaled.width = width;
    scaled.height = height;

    const scaledCtx = scaled.getContext('2d');

    scaledCtx.imageSmoothingEnabled = true;
    scaledCtx.imageSmoothingQuality = 'high';
    scaledCtx.drawImage(source, 0, 0, width, height);
    sizes.set(key, scaled);

    return scaled;
  }

  /** Template transparent-window bounds keyed by URL and canvas size. */
  const maskBoundsCache = new Map();

  /** Scratch canvas reused by every mask scan. */
  let maskCanvas = null;
  let maskCtx = null;

  /**
   * Decodes an image, forgetting it again if the load fails so a transient error
   * is not cached forever.
   *
   * @param {string} src - The image URL.
   * @param {() => void} onError - Called to drop the failed entry from its cache.
   * @returns {Promise<HTMLImageElement>} The decoded image.
   */
  function decodeImage(src, onError) {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => {
        onError();
        reject(new Error(`Failed to load ${src}`));
      };
      image.src = src;
    });
  }

  /**
   * Loads and decodes a template once, however many canvases ask for it. A product
   * page renders one canvas per gallery image, and they share their templates.
   *
   * @param {string} src - The image URL.
   * @returns {Promise<HTMLImageElement>} The decoded image.
   */
  function loadImage(src) {
    const cached = imageCache.get(src);

    if (cached) return cached;

    const promise = decodeImage(src, () => imageCache.delete(src));

    imageCache.set(src, promise);

    return promise;
  }

  /**
   * Loads the uploaded artwork once for every canvas that needs to draw it, keeping
   * only the latest upload in memory.
   *
   * @param {string} src - The uploaded image URL.
   * @returns {Promise<HTMLImageElement>} The decoded image.
   */
  function loadUploadedImage(src) {
    if (uploadCache?.src === src) return uploadCache.promise;

    const promise = decodeImage(src, () => {
      if (uploadCache?.src === src) uploadCache = null;
    });

    uploadCache = { src, promise };

    return promise;
  }

  /**
   * Finds the transparent window in a template - the area the artwork is drawn
   * into. It depends only on the template and the canvas size, so it is cached;
   * it used to be rescanned on every upload, for every canvas on the page.
   *
   * @param {HTMLImageElement} templateImage - The loaded template.
   * @param {string} src - The template URL, part of the cache key.
   * @param {number} width - The canvas width.
   * @param {number} height - The canvas height.
   * @returns {{minX: number, maxX: number, minY: number, maxY: number}} The bounds.
   */
  function getMaskBounds(templateImage, src, width, height) {
    const key = `${src}|${width}x${height}`;
    const cached = maskBoundsCache.get(key);

    if (cached) return cached;

    if (!maskCanvas) {
      maskCanvas = document.createElement('canvas');
      maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
    }

    if (maskCanvas.width !== width || maskCanvas.height !== height) {
      maskCanvas.width = width;
      maskCanvas.height = height;
    } else {
      maskCtx.clearRect(0, 0, width, height);
    }

    maskCtx.drawImage(templateImage, 0, 0, width, height);

    const pixels = maskCtx.getImageData(0, 0, width, height).data;
    const isTransparent = (x, y) => pixels[(y * width + x) * 4 + 3] < 10;

    // Walk in from each edge rather than visiting every pixel. The bounds come out
    // identical, but a full scan of a 2000px template is four million iterations.
    let minY = -1;

    for (let y = 0; y < height && minY === -1; y++) {
      for (let x = 0; x < width; x++) {
        if (isTransparent(x, y)) {
          minY = y;
          break;
        }
      }
    }

    // Fully opaque template: keep the empty bounds the callers already check for.
    if (minY === -1) {
      const empty = { minX: width, maxX: 0, minY: height, maxY: 0 };

      maskBoundsCache.set(key, empty);

      return empty;
    }

    let maxY = minY;

    for (let y = height - 1; y > minY; y--) {
      let found = false;

      for (let x = 0; x < width; x++) {
        if (isTransparent(x, y)) {
          found = true;
          break;
        }
      }

      if (found) {
        maxY = y;
        break;
      }
    }

    // Every transparent pixel sits between minY and maxY, so the columns only need
    // to be searched over those rows.
    let minX = -1;

    for (let x = 0; x < width && minX === -1; x++) {
      for (let y = minY; y <= maxY; y++) {
        if (isTransparent(x, y)) {
          minX = x;
          break;
        }
      }
    }

    let maxX = minX;

    for (let x = width - 1; x > minX; x--) {
      let found = false;

      for (let y = minY; y <= maxY; y++) {
        if (isTransparent(x, y)) {
          found = true;
          break;
        }
      }

      if (found) {
        maxX = x;
        break;
      }
    }

    const bounds = { minX, maxX, minY, maxY, edges: traceWindowEdges(isTransparent, minX, maxX, minY, maxY) };

    maskBoundsCache.set(key, bounds);

    return bounds;
  }

  /**
   * Traces the outline of the transparent window: its four corners, and for every
   * column and row the first and last transparent pixel. The mock-ups are shot at
   * an angle, so the window is a skewed quadrilateral whose edges are not quite
   * straight; following them is what lets the artwork meet the frame exactly.
   *
   * @param {(x: number, y: number) => boolean} isTransparent - Mask lookup.
   * @returns {{top: Int32Array, bottom: Int32Array, left: Int32Array, right: Int32Array,
   * corners: Record<'tl' | 'tr' | 'br' | 'bl', {x: number, y: number}>}} Profiles are
   * indexed from minX (top, bottom) and minY (left, right); -1 marks no pixel.
   */
  function traceWindowEdges(isTransparent, minX, maxX, minY, maxY) {
    const top = new Int32Array(maxX - minX + 1).fill(-1);
    const bottom = new Int32Array(maxX - minX + 1).fill(-1);
    const left = new Int32Array(maxY - minY + 1).fill(-1);
    const right = new Int32Array(maxY - minY + 1).fill(-1);

    // Corners are the extremes of x + y and x - y, which holds for a convex
    // quadrilateral whose edges lean less than 45 degrees.
    let tl = null;
    let tr = null;
    let br = null;
    let bl = null;

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (!isTransparent(x, y)) continue;

        const column = x - minX;
        const row = y - minY;

        if (top[column] === -1) top[column] = y;
        bottom[column] = y;
        if (left[row] === -1) left[row] = x;
        right[row] = x;

        if (!tl || x + y < tl.x + tl.y) tl = { x, y };
        if (!br || x + y > br.x + br.y) br = { x, y };
        if (!tr || x - y > tr.x - tr.y) tr = { x, y };
        if (!bl || x - y < bl.x - bl.y) bl = { x, y };
      }
    }

    return { top, bottom, left, right, corners: { tl, tr, br, bl } };
  }

  /** Rows and columns of the warp grid; each cell is drawn as two triangles. */
  const WARP_GRID_SIZE = 5;

  /**
   * Maps a grid over the window that follows its traced edges, pushed outward by a
   * few pixels so the artwork tucks under the frame's anti-aliased inner edge
   * instead of leaving a light seam. The template is drawn on top and hides the
   * overlap. Interior points are blended from the four edges (a Coons patch).
   *
   * @param {{minX: number, minY: number, edges: ReturnType<typeof traceWindowEdges>}} bounds
   * @param {number} bleed - How far past the traced edge to draw, in canvas pixels.
   * @returns {{x: number, y: number}[][]} Points indexed [row][column].
   */
  function buildWarpGrid({ minX, minY, edges }, bleed) {
    const { corners } = edges;
    const TL = { x: corners.tl.x - bleed, y: corners.tl.y - bleed };
    const TR = { x: corners.tr.x + bleed, y: corners.tr.y - bleed };
    const BR = { x: corners.br.x + bleed, y: corners.br.y + bleed };
    const BL = { x: corners.bl.x - bleed, y: corners.bl.y + bleed };
    const lerp = (a, b, t) => a + (b - a) * t;

    // A point along an edge, read from its traced profile where it has one.
    const edgePoint = (from, to, t, profile, offset, alongX, push) => {
      if (t === 0) return from;
      if (t === 1) return to;

      const x = lerp(from.x, to.x, t);
      const y = lerp(from.y, to.y, t);
      const traced = profile[Math.round((alongX ? x : y) - offset)];

      if (traced === undefined || traced === -1) return { x, y };

      return alongX ? { x, y: traced + push } : { x: traced + push, y };
    };

    const grid = [];

    for (let row = 0; row <= WARP_GRID_SIZE; row++) {
      const t = row / WARP_GRID_SIZE;
      const left = edgePoint(TL, BL, t, edges.left, minY, false, -bleed);
      const right = edgePoint(TR, BR, t, edges.right, minY, false, bleed);
      const points = [];

      for (let col = 0; col <= WARP_GRID_SIZE; col++) {
        const s = col / WARP_GRID_SIZE;
        const top = edgePoint(TL, TR, s, edges.top, minX, true, -bleed);
        const bottom = edgePoint(BL, BR, s, edges.bottom, minX, true, bleed);
        const blend = (key) =>
          (1 - t) * top[key] + t * bottom[key] + (1 - s) * left[key] + s * right[key] -
          ((1 - s) * (1 - t) * TL[key] + s * (1 - t) * TR[key] + (1 - s) * t * BL[key] + s * t * BR[key]);

        points.push({ x: blend('x'), y: blend('y') });
      }

      grid.push(points);
    }

    return grid;
  }

  class ImageCanvasSimulation extends HTMLElement {
    constructor() {
      super();
      this.ctx = null;
      this.currentImageData = null;
      this.uploadedImage = null;
      this.imageBounds = null;
      this.hasSubscribed = false;
    }

    connectedCallback() {
      this.canvas = this.querySelector('.paint-canvas');
      this.borderOptionsLabel = document.querySelector('[data-option-handle="border-options"] legend');
      this.borderOptionProperty = document.querySelector('[ref="border_option"]');
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });

      // The gallery moves slides in and out of the swiper wrapper, which runs this
      // callback again on the same instance. Subscribe once and stay subscribed
      // while parked, otherwise a parked canvas misses the upload that paints it.
      if (this.hasSubscribed) return;
      this.hasSubscribed = true;
      
      this.onCanvasImageUploadedUnsubscriber = subscribe(
          PUB_SUB_EVENTS.canvasImageUploaded,
          this.repopulateCanvas.bind(this)
      );

      this.onVariantIdChangedUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantIdChanged,
          this._variantIdChanged.bind(this)
      );
    }
    
    async repopulateCanvas({ data: { result, orientation } }) {
      const src = this.dataset.imageSrc;

      if (!src.includes(orientation)) return;

      const { width, height } = this.canvas;

      // RESET CANVAS
      this.ctx.save();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.clearRect(0, 0, width, height);
      this.ctx.restore();

      this.ctx.fillStyle = 'rgba(0,0,0,0)';
      this.ctx.fillRect(0, 0, width, height);

      let templateImage;
      let img;

      try {
        // Every canvas on the page asks for the same two files, so they are loaded
        // and decoded once between them instead of once per canvas.
        [templateImage, img] = await Promise.all([loadImage(src), loadUploadedImage(result)]);
      } catch (error) {
        console.error('Canvas simulation image failed to load:', error);
        return;
      }

      const maskBounds = getMaskBounds(templateImage, src, width, height);
      const { minX, maxX, minY, maxY } = maskBounds;

      this.imageBounds = { minX, maxX, minY, maxY };

      if (minX >= maxX || minY >= maxY) {
        this.ctx.drawImage(templateImage, 0, 0, width, height);
        return;
      }

      // The mirror wrap samples a fixed 20px strip of the original, so it keeps it.
      this.uploadedImage = img;

      // Warp a copy sized to the window rather than the full resolution photo.
      img = getScaledSource(img, maxX - minX, maxY - minY);

      // Follow the window's own outline rather than fixed per-template offsets, which
      // drifted from the frame and left a light seam along its inner edge.
      const bleed = Math.max(2, Math.round(Math.max(width, height) / 500));
      const grid = buildWarpGrid(maskBounds, bleed);
      const cellWidth = img.width / WARP_GRID_SIZE;
      const cellHeight = img.height / WARP_GRID_SIZE;

      this.ctx.clearRect(0, 0, width, height);

      for (let row = 0; row < WARP_GRID_SIZE; row++) {
        for (let col = 0; col < WARP_GRID_SIZE; col++) {
          const sx0 = cellWidth * col;
          const sx1 = cellWidth * (col + 1);
          const sy0 = cellHeight * row;
          const sy1 = cellHeight * (row + 1);
          const tl = grid[row][col];
          const tr = grid[row][col + 1];
          const bl = grid[row + 1][col];
          const br = grid[row + 1][col + 1];

          this.drawTriangleWarp(this.ctx, img, sx0, sy0, sx1, sy0, sx0, sy1, tl.x, tl.y, tr.x, tr.y, bl.x, bl.y);
          this.drawTriangleWarp(this.ctx, img, sx1, sy0, sx1, sy1, sx0, sy1, tr.x, tr.y, br.x, br.y, bl.x, bl.y);
        }
      }

      // DRAW TEMPLATE ON TOP
      this.ctx.drawImage(templateImage, 0, 0, width, height);

      // SAVE IMAGE DATA - only the border wrap effect ever reads these pixels back,
      // and getImageData on a full size canvas is an expensive GPU readback.
      if (src.includes('border-wrap')) {
        this.currentImageData = this.ctx.getImageData(0, 0, width, height);
      }

      this.addBorderWrap(orientation);
    }

    drawTriangleWarp(ctx, img, 
      sx1, sy1, sx2, sy2, sx3, sy3, 
      dx1, dy1, dx2, dy2, dx3, dy3) {
      
      // Round coordinates
      dx1 = Math.round(dx1); dy1 = Math.round(dy1);
      dx2 = Math.round(dx2); dy2 = Math.round(dy2);
      dx3 = Math.round(dx3); dy3 = Math.round(dy3);
      
      ctx.save();

      // **ALTERNATIVE: Extend each edge outward by 1 pixel**
      ctx.beginPath();
      
      // Function to extend a point perpendicular to an edge
      const extendEdge = (x1, y1, x2, y2, x3, y3, distance = 1) => {
        // Calculate edge vector
        const edgeX = x2 - x1;
        const edgeY = y2 - y1;
        
        // Calculate perpendicular vector (pointing outward)
        const perpX = -edgeY;
        const perpY = edgeX;
        
        // Normalize
        const length = Math.sqrt(perpX * perpX + perpY * perpY);
        const normX = perpX / length;
        const normY = perpY / length;
        
        // Extend the third point (not on this edge) outward
        return {
          x: x3 + normX * distance,
          y: y3 + normY * distance
        };
      };
      
      // Extend each vertex
      const p1 = extendEdge(dx2, dy2, dx3, dy3, dx1, dy1, 1);
      const p2 = extendEdge(dx3, dy3, dx1, dy1, dx2, dy2, 1);
      const p3 = extendEdge(dx1, dy1, dx2, dy2, dx3, dy3, 1);
      
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.lineTo(p3.x, p3.y);
      ctx.closePath();
      ctx.clip();

      // ... rest of your existing transform code
      const denom = (sx1*(sy2 - sy3) + sx2*(sy3 - sy1) + sx3*(sy1 - sy2));
      if (Math.abs(denom) < 1e-6) {
        ctx.drawImage(img, dx1, dy1);
        ctx.restore();
        return;
      }

      const a = (dx1*(sy2 - sy3) + dx2*(sy3 - sy1) + dx3*(sy1 - sy2)) / denom;
      const b = (dx1*(sx3 - sx2) + dx2*(sx1 - sx3) + dx3*(sx2 - sx1)) / denom;
      const c = (dx1*(sx2*sy3 - sx3*sy2) +
                dx2*(sx3*sy1 - sx1*sy3) +
                dx3*(sx1*sy2 - sx2*sy1)) / denom;

      const d = (dy1*(sy2 - sy3) + dy2*(sy3 - sy1) + dy3*(sy1 - sy2)) / denom;
      const e = (dy1*(sx3 - sx2) + dy2*(sx1 - sx3) + dy3*(sx2 - sx1)) / denom;
      const f = (dy1*(sx2*sy3 - sx3*sy2) +
                dy2*(sx3*sy1 - sx1*sy3) +
                dy3*(sx1*sy2 - sx2*sy1)) / denom;

      ctx.transform(a, d, b, e, c, f);
      // Smoothing off only helps when enlarging pixel art; shrinking a photo without
      // it drops most of its pixels and reads as grain. Seams are covered by the
      // 1px edge extension above.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0);

      ctx.restore();
    }
    // UPDATED: Slanted border coordinates
    getBorderCoordinates() {
      if (!this.imageBounds) return null;
      const { minX, maxX, minY, maxY } = this.imageBounds;
      const borderWidth = 
      this.dataset.imageSrc.includes('square') && this.dataset.position.includes('main') ? 18 :
      !this.dataset.imageSrc.includes('square') && this.dataset.position.includes('main') ? 15 :
      this.dataset.imageSrc.includes('square') && this.dataset.position.includes('thumbnail') ? 6 :
      !this.dataset.imageSrc.includes('square') && this.dataset.position.includes('thumbnail') ? 4 : 14;
      return {
        // DIAGONAL SLANT - top-left ubos, top-right taas; bottom-left taas, bottom-right ubos
        topRight: { 
          x: minX - borderWidth,  // 20px gikan sa wala
          y: minY + 4       // Top-left mas UBOS
        },
        topLeft: { 
          x: minX + 1,      // Connect to image start
          y: minY + 1       // Top-right LEVEL sa image top
        },
        bottomRight: { 
          x: minX - borderWidth,  // 20px gikan sa wala
          y: maxY - 2       // Bottom-left mas TAAS
        },
        bottomLeft: { 
          x: minX + 1,      // Connect to image start
          y: maxY + 3       // Bottom-right LEVEL sa image bottom
        }
      };
    }

    addBorderWrap(orientation) {
      if(!this.dataset.imageSrc.includes('border-wrap') || !this.dataset.imageSrc.includes(orientation)) return;
      if (this.currentImageData) {
        this.ctx.putImageData(this.currentImageData, 0, 0);
      }
      if (this.uploadedImage) {
        this.addMirrorEffect(orientation);
      } else {
        this.drawSolidBorder();
      }

      this.currentImageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    }

    drawSolidBorder() {
      const coords = this.getBorderCoordinates();
      if (!coords) return; 
      this.ctx.fillStyle = '#000000';
      this.ctx.beginPath();
      this.ctx.moveTo(coords.topLeft.x, coords.topLeft.y);
      this.ctx.lineTo(coords.topRight.x, coords.topRight.y);
      this.ctx.lineTo(coords.bottomRight.x, coords.bottomRight.y);
      this.ctx.lineTo(coords.bottomLeft.x, coords.bottomLeft.y);
      this.ctx.closePath();
      this.ctx.fill();
    }

    // UPDATED: Mirror effect for slanted border
    addMirrorEffect(orientation) {
      if(!this.dataset.imageSrc.includes('border-wrap') || !this.uploadedImage || !this.dataset.imageSrc.includes(orientation)) return;
      
      if (this.currentImageData) {
        this.ctx.putImageData(this.currentImageData, 0, 0);
      }

      const { width, height } = this.canvas;
      const mirrorCanvas = document.createElement('canvas');
      mirrorCanvas.width = width;
      mirrorCanvas.height = height;
      const mirrorCtx = mirrorCanvas.getContext('2d');

      const coords = this.getBorderCoordinates();
      if (!coords) return;

      mirrorCtx.save();
      mirrorCtx.beginPath();
      mirrorCtx.moveTo(coords.topLeft.x, coords.topLeft.y);
      mirrorCtx.lineTo(coords.topRight.x, coords.topRight.y);
      mirrorCtx.lineTo(coords.bottomRight.x, coords.bottomRight.y);
      mirrorCtx.lineTo(coords.bottomLeft.x, coords.bottomLeft.y);
      mirrorCtx.closePath();
      mirrorCtx.clip();

      // USE TRIANGLE WARP FOR SLANTED IMAGE
      const mirrorTriangles = [
        {
          src: [0, 0, 20, 0, 0, this.uploadedImage.height],
          dst: [
            coords.topLeft.x, coords.topLeft.y - 10,                    // Top-right: original
            coords.topRight.x, coords.topRight.y - 10,            // ↑ Top-left 
            coords.bottomLeft.x, coords.bottomLeft.y - 2            // Top-middle: original
          ]
        },
        {
          src: [20, 0, 20, this.uploadedImage.height, 0, this.uploadedImage.height],
          dst: [
            coords.topRight.x, coords.topRight.y - 10,            // ↑ bottom-middle PASATAAS  
            coords.bottomRight.x, coords.bottomRight.y + 10,      // ↓ Bottom-left PAUBSON
            coords.bottomLeft.x, coords.bottomLeft.y - 2       //  Bottom right: original
          ]
        }
      ];

      mirrorCtx.scale(-1, 1); // Flip horizontally

      mirrorTriangles.forEach(tri => {
        // Adjust X coordinates for flip
        const flippedDst = tri.dst.map((val, index) => 
          index % 2 === 0 ? -val : val
        );
        
        this.drawTriangleWarp(
          mirrorCtx, this.uploadedImage,
          ...tri.src,
          ...flippedDst
        );
      });

      mirrorCtx.restore();
      this.ctx.drawImage(mirrorCanvas, 0, 0, width, height);
      this.currentImageData = this.ctx.getImageData(0, 0, width, height);
    }

    _variantIdChanged({ data: { event }}) {
      const orientationHandle = document.querySelector('[data-option-handle="orientation"] input:checked');
      const orientationLowerCase = orientationHandle.value.toLowerCase();
      const selectedBorder = PUB_SUB_BORDER_OPTIONS.find(border => border.title.value === event.target.value); 
      
      if(this.borderOptionsLabel && selectedBorder) {
        this.borderOptionsLabel.innerHTML = `<p><strong><span class="step-name">Step ${this.borderOptionsLabel.dataset.step}:</span> Border Options: </strong>${selectedBorder.title.value}</p>`;
        this.borderOptionProperty.name = 'properties[Border Option]';
        this.borderOptionProperty.value = selectedBorder.title.value;
      } 
      
      if (!selectedBorder || !this.dataset.imageSrc.includes('border-wrap') || !this.dataset.imageSrc.includes(orientationLowerCase)) return;
      
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      
      if (this.originalImageData) {
        this.ctx.putImageData(this.originalImageData, 0, 0);
      } else if (this.currentImageData) {
        this.ctx.putImageData(this.currentImageData, 0, 0);
      }
      
      const coords = this.getBorderCoordinates();
      if (!coords) {
        this.createCanvas(orientationLowerCase);
        return;
      }
      
      if (selectedBorder.title.value === "Mirror Wrap") {
        if (this.uploadedImage) {
          this.addMirrorEffect(orientationLowerCase);
        } else {
          this.ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
          this.ctx.beginPath();
          this.ctx.moveTo(coords.topLeft.x, coords.topLeft.y);
          this.ctx.lineTo(coords.topRight.x, coords.topRight.y);
          this.ctx.lineTo(coords.bottomRight.x, coords.bottomRight.y);
          this.ctx.lineTo(coords.bottomLeft.x, coords.bottomLeft.y);
          this.ctx.closePath();
          this.ctx.fill();
        }
      } else {
        this.ctx.fillStyle = selectedBorder.color.value;
        this.ctx.beginPath();
        this.ctx.moveTo(coords.topLeft.x, coords.topLeft.y);
        this.ctx.lineTo(coords.topRight.x, coords.topRight.y);
        this.ctx.lineTo(coords.bottomRight.x, coords.bottomRight.y);
        this.ctx.lineTo(coords.bottomLeft.x, coords.bottomLeft.y);
        this.ctx.closePath();
        this.ctx.fill();
      }
    }

    async createCanvas(orientation) {
      const src = this.dataset.imageSrc;

      if (!src.includes(orientation)) return;

      const { width, height } = this.canvas;

      this.ctx.clearRect(0, 0, width, height);

      let templateImage;

      try {
        templateImage = await loadImage(src);
      } catch (error) {
        console.error('Canvas simulation template failed to load:', error);
        return;
      }

      const { minX, maxX, minY, maxY } = getMaskBounds(templateImage, src, width, height);

      this.imageBounds = { minX, maxX, minY, maxY };

      if (minX >= maxX || minY >= maxY) {
        this.ctx.drawImage(templateImage, 0, 0, width, height);
      }
    }
  }

  customElements.define('image-canvas-simulation', ImageCanvasSimulation);
}